import type { SearchOption } from './SearchSelect';

// Codes shared by the store and bin pickers: a fixed prefix and a number.

/** A code without case, dashes or spaces: "st-03 " → "ST03". */
function squash(text: string): string {
  return text.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** "ST03" → letters "ST", number 3; null for a code that is not letters then digits. */
function codeParts(text: string): { letters: string; num: number } | null {
  const m = /^([A-Z]*)(\d+)$/.exec(squash(text));
  return m ? { letters: m[1]!, num: Number(m[2]) } : null;
}

/** Codes are a fixed prefix and a number (ST03), so people type the number:
 *  "3", "03", "st3" and "ST-03" all find ST03. */
export function matchCode(option: SearchOption, query: string): boolean {
  const q = squash(query);
  if (!q || squash(option.label).includes(q)) return true;
  const a = codeParts(option.label);
  const b = codeParts(query);
  return a !== null && b !== null && a.letters.startsWith(b.letters) && a.num === b.num;
}

/** The code typed in full, in any of the ways `matchCode` accepts. Only ever
 *  taken when exactly one option fits, so "3" never guesses between ST03 and RC03. */
export function sameCode(option: SearchOption, query: string): boolean {
  if (squash(option.label) === squash(query)) return true;
  const a = codeParts(option.label);
  const b = codeParts(query);
  return a !== null && b !== null && (b.letters === '' || b.letters === a.letters) && a.num === b.num;
}

/** Natural order, ignoring case: ST-2 before ST-10, "feed" beside "Feed". */
export function compare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}
